import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CloudCheck,
  Leaf,
  Loader2,
  MessageCircleHeart,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

const GOAL_OPTIONS = [
  { key: "PHY", label: "My physical energy and daily rhythms" },
  { key: "ENV", label: "My home and surroundings" },
  { key: "EMO", label: "My emotional load and stress" },
  { key: "SPI", label: "My sense of meaning and connection" },
  { key: "unsure", label: "I'm not sure yet" },
];

const READINESS_OPTIONS = [
  { key: "high", label: "I'm ready to begin" },
  { key: "medium", label: "I'm considering it" },
  { key: "low", label: "I'm just exploring" },
];

const WORKED_OPTIONS = [
  { key: "yes", label: "Yes, regularly" },
  { key: "somewhat", label: "Somewhat" },
  { key: "not_yet", label: "Not yet, and that's okay" },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Assessment() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("intro");
  const [answers, setAnswers] = useState({});
  const [goal, setGoal] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [idx, setIdx] = useState(0);
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState("saved");
  const [advancing, setAdvancing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [prevIntention, setPrevIntention] = useState(null);
  const [reflectOpen, setReflectOpen] = useState(false);
  const [reflectWorked, setReflectWorked] = useState("yes");
  const [reflectNote, setReflectNote] = useState("");
  const [reflectSaving, setReflectSaving] = useState(false);
  const [reflectDone, setReflectDone] = useState(false);
  const loaded = useRef(false);
  const advanceTimer = useRef(null);
  const submitGuard = useRef(false);
  const topRef = useRef(null);

  useEffect(() => {
    document.title = "Your Wellness Discovery | Rooted Healing";
    (async () => {
      try {
        const [q, d] = await Promise.all([
          api.get("/assessment/questions"),
          api.get("/assessment/draft"),
        ]);
        setData(q.data);
        const saved = d.data?.responses || {};
        if (Object.keys(saved).length > 0) {
          setAnswers(saved);
          setGoal((d.data.goals || [])[0] || null);
          setReadiness(d.data.readiness || null);
          const firstOpen = q.data.questions.findIndex((x) => !saved[x.id]);
          setIdx(firstOpen === -1 ? q.data.questions.length - 1 : firstOpen);
          setPhase(firstOpen === -1 ? "review" : "questions");
          toast.success("Welcome back. We restored your saved progress.");
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail, "Could not load your discovery."));
      }
      try {
        const { data: inv } = await api.get("/intentions");
        const open = (inv.intentions || []).find(
          (i) =>
            !["archived", "completed"].includes(i.status) &&
            (i.reflections || []).length === 0 &&
            i.assessment_id
        );
        if (open) setPrevIntention(open);
      } catch {
        /* intentions are optional here */
      }
      loaded.current = true;
    })();
  }, []);

  // Keep every new question anchored at the top of its container, clear of the
  // fixed site header, on every transition (intro -> questions, between
  // questions, between dimensions, into review or preparing).
  useEffect(() => {
    if (!data) return;
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [phase, idx, data]);

  useEffect(() => {
    if (!loaded.current || !data) return;
    setSaving("saving");
    const t = setTimeout(async () => {
      try {
        await api.post("/assessment/draft", {
          responses: answers,
          goals: goal && goal !== "unsure" ? [goal] : [],
          readiness,
        });
      } catch {
        /* draft saving is best-effort; never block the member */
      }
      setSaving("saved");
    }, 700);
    return () => clearTimeout(t);
  }, [answers, goal, readiness, data]);

  if (!data) {
    return (
      <main className="flex min-h-screen items-center justify-center pt-[76px]">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </main>
    );
  }

  const questions = data.questions;
  const categories = data.categories;
  const catOrder = [...new Set(questions.map((q) => q.category))];
  const catName = (key) => categories.find((c) => c.key === key)?.name || key;
  const answeredCount = Object.keys(answers).length;
  const q = questions[idx];
  const isLast = idx === questions.length - 1;
  const selected = answers[q?.id];

  const goTo = (next) => {
    clearTimeout(advanceTimer.current);
    setAdvancing(false);
    setDirection(next > idx ? 1 : -1);
    setIdx(next);
  };

  const submit = async (finalAnswers) => {
    if (submitGuard.current) return;
    submitGuard.current = true;
    setSubmitting(true);
    setSubmitError(null);
    setPhase("preparing");
    try {
      const { data: res } = await api.post("/assessment/submit", {
        responses: finalAnswers,
        goals: goal && goal !== "unsure" ? [goal] : [],
        readiness,
      });
      track("assessment_completed");
      navigate(`/results/${res.assessment_id}`);
    } catch (e) {
      submitGuard.current = false;
      setSubmitting(false);
      setSubmitError(
        formatApiError(e.response?.data?.detail, "Could not save your discovery.")
      );
    }
  };

  const finalize = (finalAnswers) => {
    clearTimeout(advanceTimer.current);
    submit(finalAnswers);
  };

  const selectAnswer = (answerId) => {
    if (advancing) return;
    const next = { ...answers, [q.id]: answerId };
    setAnswers(next);
    setAdvancing(true);
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      setAdvancing(false);
      if (isLast) {
        // Final answer given: calculate and reveal results automatically.
        finalize(next);
      } else {
        goTo(idx + 1);
      }
    }, isLast ? 550 : 420);
  };

  const saveReflection = async () => {
    setReflectSaving(true);
    try {
      await api.post(`/intentions/${prevIntention.intention_id}/reflections`, {
        worked_on: reflectWorked,
        note: reflectNote.trim() || null,
      });
      setReflectDone(true);
      toast.success("Reflection saved. Thank you for looking back gently.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not save that reflection."));
    }
    setReflectSaving(false);
  };

  return (
    <main data-testid="assessment-page" className="min-h-screen pt-[76px]">
      <div className="mx-auto max-w-2xl px-5 py-12 sm:px-8 sm:py-16">
        <div ref={topRef} className="scroll-mt-28" aria-hidden="true" />
        <div className="mb-10">
          <div className="flex items-center justify-between text-xs text-taupe">
            <span data-testid="assessment-progress-label">
              {answeredCount} of {questions.length} explored
            </span>
            <span className="flex items-center gap-1.5" data-testid="assessment-save-state">
              {saving === "saving" ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving
                </>
              ) : (
                <>
                  <CloudCheck className="h-3.5 w-3.5 text-forest" /> Saved automatically
                </>
              )}
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-forest/10">
            <motion.div
              className="h-full rounded-full bg-gold"
              animate={{ width: `${(answeredCount / questions.length) * 100}%` }}
              transition={{ duration: 0.5, ease: EASE }}
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {catOrder.map((c) => {
              const total = questions.filter((x) => x.category === c).length;
              const done = questions.filter((x) => x.category === c && answers[x.id]).length;
              return (
                <span
                  key={c}
                  data-testid={`assessment-dimension-chip-${c.toLowerCase()}`}
                  className={`rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide transition-colors duration-300 ${
                    done === total
                      ? "bg-forest text-ivory"
                      : done > 0
                        ? "bg-sage text-forest"
                        : "bg-forest/5 text-taupe"
                  }`}
                >
                  {catName(c)} · {done}/{total}
                </span>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {phase === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: EASE }}
              data-testid="assessment-intro"
              className="rounded-[2.5rem] border border-forest/10 bg-white/75 p-8 shadow-[0_20px_50px_rgba(23,63,53,0.1)] sm:p-11"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">
                Before We Begin
              </p>
              <h1 className="mt-4 font-serif text-3xl leading-tight text-forest sm:text-4xl">
                Your Rooted Wellness Discovery
              </h1>
              <p className="mt-4 leading-relaxed text-charcoal/70">
                There are no right answers here. Move at your own pace, go back whenever you
                like, and skip anything that feels too personal. Your progress saves
                automatically, and your answers stay private to your account.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-taupe">
                This is an educational, self-reported reflection. It is not a medical diagnosis
                or a measurement of your health.
              </p>

              {prevIntention && !reflectDone && (
                <div
                  data-testid="assessment-reflection-invite"
                  className="mt-8 rounded-[1.75rem] border border-gold/30 bg-sage/50 p-6"
                >
                  <div className="flex items-center gap-2 text-gold-dark">
                    <MessageCircleHeart className="h-4 w-4" />
                    <p className="text-xs font-semibold uppercase tracking-[0.2em]">
                      A Gentle Look Back
                    </p>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/75">
                    During your last discovery, you chose to focus on this intention:
                  </p>
                  <p className="mt-2 font-serif text-sm italic leading-relaxed text-forest">
                    "{prevIntention.text}"
                  </p>
                  {reflectOpen ? (
                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-forest">
                        Did you get to work on it? There is no wrong answer.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {WORKED_OPTIONS.map((o) => (
                          <button
                            key={o.key}
                            data-testid={`invite-reflect-${o.key}`}
                            onClick={() => setReflectWorked(o.key)}
                            className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                              reflectWorked === o.key
                                ? "border-gold bg-gold/15 text-forest"
                                : "border-forest/20 text-charcoal/70 hover:border-gold/60"
                            }`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                      <textarea
                        data-testid="invite-reflection-note"
                        value={reflectNote}
                        onChange={(e) => setReflectNote(e.target.value)}
                        rows={2}
                        maxLength={1000}
                        placeholder="Anything you noticed? Optional, and only you can see this."
                        className="mt-3 w-full rounded-2xl border border-forest/20 bg-white/70 px-4 py-3 text-sm outline-none focus:border-gold"
                      />
                      <div className="mt-3 flex flex-wrap items-center gap-4">
                        <button
                          data-testid="invite-reflection-save"
                          onClick={saveReflection}
                          disabled={reflectSaving}
                          className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-xs font-semibold text-ivory transition-colors hover:bg-forest-deep disabled:opacity-50"
                        >
                          {reflectSaving ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                          Save my reflection
                        </button>
                        <button
                          data-testid="invite-reflection-cancel"
                          onClick={() => setReflectOpen(false)}
                          className="text-xs font-semibold text-taupe underline-offset-4 hover:underline"
                        >
                          Not now
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 flex flex-wrap items-center gap-4">
                      <button
                        data-testid="invite-reflect-open"
                        onClick={() => setReflectOpen(true)}
                        className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-5 py-2.5 text-xs font-semibold text-forest transition-colors hover:border-gold"
                      >
                        Reflect on it briefly
                      </button>
                      <button
                        data-testid="invite-reflect-skip"
                        onClick={() => setReflectDone(true)}
                        className="text-xs font-semibold text-taupe underline-offset-4 hover:underline"
                      >
                        Skip for now
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-9">
                <h2 className="font-serif text-xl text-forest">
                  What would you most like to nurture right now?
                </h2>
                <div className="mt-4 flex flex-col gap-2.5">
                  {GOAL_OPTIONS.map((g) => (
                    <button
                      key={g.key}
                      data-testid={`assessment-goal-${g.key.toLowerCase()}`}
                      onClick={() => setGoal(g.key)}
                      className={`rounded-2xl border px-5 py-3.5 text-left text-sm transition-all duration-300 ${
                        goal === g.key
                          ? "border-gold bg-gold/10 font-semibold text-forest"
                          : "border-forest/15 bg-white/60 text-charcoal/75 hover:border-gold/50"
                      }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <h2 className="font-serif text-xl text-forest">
                  How ready do you feel to make small changes?
                </h2>
                <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
                  {READINESS_OPTIONS.map((r) => (
                    <button
                      key={r.key}
                      data-testid={`assessment-readiness-${r.key}`}
                      onClick={() => setReadiness(r.key)}
                      className={`flex-1 rounded-2xl border px-5 py-3.5 text-sm transition-all duration-300 ${
                        readiness === r.key
                          ? "border-gold bg-gold/10 font-semibold text-forest"
                          : "border-forest/15 bg-white/60 text-charcoal/75 hover:border-gold/50"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-taupe">
                  Interim wording · pending final blueprint approval
                </p>
              </div>

              <button
                data-testid="assessment-begin-button"
                onClick={() => {
                  track("assessment_started");
                  setPhase("questions");
                }}
                disabled={!goal || !readiness}
                className="group mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep disabled:opacity-50"
              >
                Begin My Discovery
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>
          )}

          {phase === "questions" && q && (
            <motion.div
              key={q.id}
              custom={direction}
              initial={{ opacity: 0, x: 56 * direction }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -56 * direction }}
              transition={{ duration: 0.4, ease: EASE }}
              data-testid={`assessment-question-${q.id.toLowerCase()}`}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">
                Part {catOrder.indexOf(q.category) + 1} of {catOrder.length} · {catName(q.category)}
              </p>
              <h1 className="mt-4 font-serif text-2xl leading-snug text-forest sm:text-3xl">
                {q.text}
              </h1>
              <div className="mt-8 flex flex-col gap-3">
                {q.answers.map((a) => (
                  <button
                    key={a.id}
                    data-testid={`answer-${a.id.toLowerCase()}`}
                    onClick={() => selectAnswer(a.id)}
                    disabled={advancing}
                    className={`group flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-all duration-300 disabled:opacity-80 ${
                      selected === a.id
                        ? "border-gold bg-gold/10 shadow-[0_10px_25px_rgba(180,146,83,0.18)]"
                        : "border-forest/12 bg-white/70 hover:border-gold/50 hover:bg-white"
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-bold transition-colors duration-300 ${
                        selected === a.id
                          ? "border-gold bg-gold text-forest-deep"
                          : "border-forest/20 text-forest/60 group-hover:border-gold/60"
                      }`}
                    >
                      {selected === a.id ? <Check className="h-4 w-4" /> : a.letter}
                    </span>
                    <span className="text-sm leading-relaxed text-charcoal/85 sm:text-base">
                      {a.text}
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-8 flex items-center justify-between">
                <button
                  data-testid="assessment-back-button"
                  onClick={() => (idx === 0 ? setPhase("intro") : goTo(idx - 1))}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-forest transition-colors hover:text-gold-dark"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <div className="flex items-center gap-5">
                  {q.sensitive && (
                    <button
                      data-testid="assessment-skip-button"
                      onClick={() => (isLast ? finalize(answers) : goTo(idx + 1))}
                      className="text-sm text-taupe underline-offset-4 transition-colors hover:text-forest hover:underline"
                    >
                      Prefer to skip
                    </button>
                  )}
                  {!isLast && (
                    <button
                      data-testid="assessment-next-button"
                      onClick={() => goTo(idx + 1)}
                      disabled={!selected && !q.sensitive}
                      className="group inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep disabled:opacity-50"
                    >
                      Continue
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  )}
                </div>
              </div>
              {isLast && (
                <p className="mt-5 text-center text-xs text-taupe">
                  This is the last question. Your results will prepare themselves the moment you
                  answer.
                </p>
              )}
            </motion.div>
          )}

          {phase === "preparing" && (
            <motion.div
              key="preparing"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: EASE }}
              data-testid="assessment-preparing"
              className="flex flex-col items-center rounded-[2.5rem] border border-forest/10 bg-white/75 p-12 text-center shadow-[0_20px_50px_rgba(23,63,53,0.1)]"
            >
              {submitError ? (
                <>
                  <p className="font-serif text-2xl text-forest">A small pause</p>
                  <p data-testid="assessment-submit-error" className="mt-3 max-w-sm text-sm leading-relaxed text-charcoal/70">
                    {submitError} Your answers are saved safely, and nothing is lost.
                  </p>
                  <button
                    data-testid="assessment-retry-button"
                    onClick={() => submit(answers)}
                    disabled={submitting}
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep disabled:opacity-50"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Try Again
                  </button>
                </>
              ) : (
                <>
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sage">
                    <Leaf className="h-7 w-7 animate-pulse text-forest" />
                  </span>
                  <p className="mt-6 font-serif text-2xl leading-snug text-forest sm:text-3xl">
                    Preparing Your Rooted Wellness Profile...
                  </p>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-charcoal/65">
                    Gathering your answers into a personal reflection. This takes only a moment.
                  </p>
                  <Loader2 className="mt-6 h-5 w-5 animate-spin text-gold" />
                </>
              )}
            </motion.div>
          )}

          {phase === "review" && (
            <motion.div
              key="review"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: EASE }}
              data-testid="assessment-review"
              className="rounded-[2.5rem] border border-forest/10 bg-white/75 p-8 shadow-[0_20px_50px_rgba(23,63,53,0.1)] sm:p-11"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">
                One Last Look
              </p>
              <h1 className="mt-4 font-serif text-3xl text-forest">Review your discovery</h1>
              <p className="mt-3 leading-relaxed text-charcoal/70">
                You explored {answeredCount} of {questions.length} questions. Anything you
                skipped simply stays out of your results.
              </p>
              <div className="mt-7 flex flex-col gap-3">
                {catOrder.map((c) => {
                  const qs = questions.filter((x) => x.category === c);
                  const open = qs.filter((x) => !answers[x.id]);
                  return (
                    <div
                      key={c}
                      className="flex items-center justify-between rounded-2xl border border-forest/10 px-5 py-4"
                    >
                      <div>
                        <p className="text-sm font-semibold text-forest">{catName(c)}</p>
                        <p className="text-xs text-taupe">
                          {qs.length - open.length} of {qs.length} answered
                          {open.length > 0 && ` · ${open.length} skipped`}
                        </p>
                      </div>
                      {open.length > 0 && (
                        <button
                          data-testid={`review-revisit-${c.toLowerCase()}`}
                          onClick={() => {
                            setPhase("questions");
                            goTo(questions.findIndex((x) => x.id === open[0].id));
                          }}
                          className="text-xs font-semibold text-gold-dark underline-offset-4 hover:underline"
                        >
                          Revisit
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-9 flex flex-wrap items-center justify-between gap-4">
                <button
                  data-testid="review-back-button"
                  onClick={() => {
                    setPhase("questions");
                    goTo(questions.length - 1);
                  }}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-forest transition-colors hover:text-gold-dark"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Keep editing
                </button>
                <button
                  data-testid="assessment-submit-button"
                  onClick={() => submit(answers)}
                  disabled={submitting || answeredCount === 0}
                  className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-forest-deep transition-all duration-300 hover:bg-gold-soft disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  See My Wellness Profile
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
